import { uniqBy } from 'lodash';
import { baseApi } from 'core/api';

const state = () => ({
    _items: null,
    _more: null,
    _total: 0,
    _search: {
        items: null,
        more: null,
        total: 0,
        term: null
    }
});

const getters = {
    scheduledMessages: (state) => {
        if (state._search.term !== null) {
            return state._search.items;
        } else {
            return state._items;
        }
    },
    scheduledMessagesTotal: (state) => {
        if (state._search.term !== null) {
            return state._search.total;
        } else {
            return state._total;
        }
    },
    scheduledMessagesHaveMore: (state) => {
        if (state._search.term !== null) {
            return state._search.more !== null;
        } else {
            return state._more !== null;
        }
    },
    scheduledMessagesSearchTerm: (state) => state._search.term
};

const actions = {
    /**
     * Loads the scheduledMessages from the backend.
     *
     * @param {Boolean} retry
     * @return {Promise}
     */
    scheduledMessagesLoad(retry) {
        let url = `core/messages/scheduled`;

        if (!retry && this._more) {
            url = this._more;
        } else if (retry === true) {
            this._setScheduledMessagesRetryLoading();
        }

        return baseApi
            .get(url)
            .then((response) => {
                this._setScheduledMessagesLoaded(response.data);

                return response;
            })
            .catch(() => {
                this._setScheduledMessagesErrorLoading();
            });
    },

    /**
     * Search the scheduledMessages.
     *
     * @param {String} term
     * @return {Promise}
     */
    scheduledMessagesSearch(term) {
        let url = `core/messages/scheduled`;

        if (this._search.more) {
            url = this._search.more;
        }

        return baseApi
            .get(url, { params: { term: term } })
            .then((response) => {
                this._setScheduledMessagesSearchLoaded({
                    term: term,
                    data: response.data
                });

                return response;
            })
            .catch((errors) => {
                this._setScheduledMessagesSearchError();

                return errors;
            });
    },

    /**
     * Clears the search results.
     *
     * @return {void}
     */
    scheduledMessagesClearSearch() {
        this._setScheduledMessagesSearchCleared();
    },

    /**
     * Creates a scheduledMessage using the given form.
     *
     * @param {Form} form
     * @return {Promise}
     */
    scheduledMessagesCreate(form) {
        return form
            .post(`core/messages/scheduled`)
            .then((response) => {
                this._setScheduledMessageCreated(response.data);

                return response;
            });
    },

    /**
     * Updates a scheduledMessage using the given form.
     *
     * @param {Number} id
     * @param {Form} form
     * @return {Promise}
     */
    scheduledMessagesUpdate({ id, form }) {
        return form
            .put(`core/messages/scheduled/${id}`)
            .then((response) => {
                this._setScheduledMessageUpdated(response.data);

                return response;
            });
    },

    /**
     * Deletes the given scheduledMessage.
     *
     * @param {Number} id
     * @param {Form} form
     * @return {Promise}
     */
    scheduledMessagesDelete({ id, form }) {
        return form
            .delete(`core/messages/${id}`)
            .then((response) => {
                this._setScheduledMessageDeleted(id);

                return response;
            });
    },

    /**
     * Bulk deletes the given scheduledMessages.
     *
     * @param {Form} form
     * @return {Promise}
     */
    scheduledMessagesBulkDelete(form) {
        return form
            .delete(`core/messages/scheduled`)
            .then((response) => {
                this._setScheduledMessagesBulkDeleted(form.ids);

                return response;
            });
    },

    /**
     * Adds the given scheduledMessage to the stack.
     *
     * @param {Object} scheduledMessage
     * @return {void}
     */
    scheduledMessageCreated(scheduledMessage) {
        this._setScheduledMessageCreated(scheduledMessage);
    },

    /**
     * Triggered when a scheduledMessage is updated.
     *
     * @param {Object} scheduledMessage
     * @return {void}
     */
    scheduledMessageUpdated(scheduledMessage) {
        this._setScheduledMessageUpdated(scheduledMessage);
    },

    /**
     * Removes the given scheduledMessage ID from the list.
     *
     * @param {Number} id
     * @return {void}
     */
    scheduledMessageDeleted({ id }) {
        this._setScheduledMessageDeleted(id);
    },

    /**
     * Removes the given scheduledMessage IDs from the list.
     *
     * @param {Number} ids
     * @return {void}
     */
    scheduledMessagesBulkDeleted(ids) {
        this._setScheduledMessagesBulkDeleted(ids);
    },

    // Private methods for all operations
    _setScheduledMessageCreated(data) {
        if (this._items === null) {
            return;
        }

        this._items.unshift(data);
        ++this._total;
    },

    _setScheduledMessageUpdated(data) {
        if (this._items === null) {
            return;
        }

        this._items = this._items.map((o) => {
            if (o.id === data.id) {
                return data;
            } else {
                return o;
            }
        });

        if (this._search.results) {
            this._search.results = this._search.results.map((o) => {
                if (o.id === data.id) {
                    return data;
                } else {
                    return o;
                }
            });
        }
    },

    _setScheduledMessageDeleted(id) {
        if (!this._items) return;

        this._items = this._items.filter((o) => o.id !== id);
        --this._total;

        if (this._search.results) {
            this._search.results = this._search.results.filter((o) => o.id !== id);
        }
    },

    _setScheduledMessagesBulkDeleted(ids) {
        if (!this._items) return;

        this._items = this._items.filter((o) => {
            return ids.indexOf(o.id) === -1;
        });

        this._total = this._total - ids.length;

        if (this._search.results) {
            this._search.results = this._search.results.filter((o) => {
                return ids.indexOf(o.id) === -1;
            });
        }
    },

    _setScheduledMessagesLoaded(data) {
        if (this._more) {
            for (let i in data.data) {
                this._items.push(data.data[i]);
            }
        } else {
            this._items = data.data;
        }

        this._more = data.next_page_url;
        this._total = data.total;
    },

    _setScheduledMessagesRetryLoading() {
        this._items = null;
        this._more = null;
        this._total = 0;
        this._search = {
            items: null,
            more: null,
            total: 0,
            term: null
        };
    },

    _setScheduledMessagesErrorLoading() {
        this._items = false;
        this._more = null;
        this._total = 0;
        this._search = {
            items: null,
            more: null,
            total: 0,
            term: null
        };
    },

    _setScheduledMessagesSearchLoaded({ term, data }) {
        if (this._search.items === null) {
            this._search.items = data.data;
        } else {
            this._search.items = [];

            for (let i in data.data) {
                this._search.items.push(data.data[i]);
            }

            this._search.items = uniqBy(this._search.items, 'id');
        }

        this._search.more = data.next_page_url;
        this._search.total = data.total;
        this._search.term = term;
    },

    _setScheduledMessagesSearchError() {
        this._search = {
            items: null,
            more: null,
            total: 0,
            term: null
        };
    },

    _setScheduledMessagesSearchCleared() {
        this._search = {
            items: null,
            more: null,
            total: 0,
            term: null
        };
    }
};

export default {
    state,
    getters,
    actions
};
